import React, { useRef, useEffect, useState, useCallback, useImperativeHandle, forwardRef, useMemo } from "react";
import "./pdfViewer.scss";

const PDFDrawCanvas = forwardRef(({
  pdfDoc,
  totalPages,
  isDrawingMode,
  selectedTool,
  selectedColor,
  brushSize,
  pageRefs,
  theme = "light",
  onDrawingsChange,
}, ref) => {
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentPath, setCurrentPath] = useState([]);
  const [drawings, setDrawings] = useState({}); // Store unsaved drawings per page: { pageNum: [{ tool, color, path, type }] }
  const [drawingHistory, setDrawingHistory] = useState([{}]); // History of drawings states for undo/redo
  const [historyIndex, setHistoryIndex] = useState(0); // Current position in history
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0, visible: false });
  const cursorRef = useRef(null);
  const currentDrawingPageRef = useRef(null);
  const prevPdfDocRef = useRef(null);
  const isUndoRedoRef = useRef(false); // Flag to prevent adding to history during undo/redo
  const lastHistoryStateRef = useRef(null); // Track last state added to history to avoid duplicates
  const prevDrawingsBeforeUndoRedoRef = useRef(null); // Track drawings state before undo/redo to redraw cleared pages

  // Clear drawings when PDF document changes (new PDF loaded)
  useEffect(() => {
    // Only clear if pdfDoc changed (not on initial mount)
    if (pdfDoc && prevPdfDocRef.current !== null && prevPdfDocRef.current !== pdfDoc) {
      setDrawings({});
      setIsDrawing(false);
      setCurrentPath([]);
      currentDrawingPageRef.current = null;
      
      // Clear drawing canvases
      if (pageRefs.current) {
        pageRefs.current.forEach((pageRef) => {
          const page = pageRef?.current;
          if (page) {
            const drawCanvas = page.querySelector(".pdf-draw-canvas");
            if (drawCanvas) {
              const ctx = drawCanvas.getContext("2d");
              ctx.clearRect(0, 0, drawCanvas.width, drawCanvas.height);
            }
          }
        });
      }
      
      // Notify parent that unsaved drawings are cleared
      if (onDrawingsChange) {
        onDrawingsChange(false);
      }
    }
    prevPdfDocRef.current = pdfDoc;
  }, [pdfDoc, pageRefs, onDrawingsChange]);

  // Helper function to create/update drawing canvas for a single page (moved up for use in getDrawingCanvas)
  const setupDrawingCanvasForPage = useCallback((pageNum) => {
    const pageRef = pageRefs.current[pageNum - 1]?.current;
    if (!pageRef) return null;

    // Check if drawing canvas already exists
    let drawCanvas = pageRef.querySelector(".pdf-draw-canvas");
    const wasNewCanvas = !drawCanvas;
    
    if (!drawCanvas) {
      drawCanvas = document.createElement("canvas");
      drawCanvas.className = "pdf-draw-canvas";
      drawCanvas.style.position = "absolute";
      drawCanvas.style.top = "0";
      drawCanvas.style.left = "0";
      drawCanvas.style.pointerEvents = "auto";
      drawCanvas.style.zIndex = "10"; // Above saved drawings
      pageRef.style.position = "relative";
      pageRef.appendChild(drawCanvas);
    }

    // Use PDF canvas dimensions if available, otherwise use page container
    const pdfCanvas = pageRef.querySelector(".pdf-canvas");
    let rect;
    let canvasWidth;
    let canvasHeight;
    
    if (pdfCanvas && pdfCanvas.width > 0 && pdfCanvas.height > 0) {
      // PDF canvas is rendered, use its dimensions
      rect = pdfCanvas.getBoundingClientRect();
      canvasWidth = pdfCanvas.width;
      canvasHeight = pdfCanvas.height;
    } else {
      // PDF canvas not rendered yet, use page container dimensions
      // This allows drawing immediately on large PDFs
      rect = pageRef.getBoundingClientRect();
      canvasWidth = rect.width;
      canvasHeight = rect.height;
    }
    
    // Set internal canvas dimensions
    // For new canvases, always set. For existing, only if not currently drawing on this page
    // Setting width/height clears the canvas, so we avoid it during active drawing
    const isCurrentlyDrawingThisPage = currentDrawingPageRef.current === pageNum;
    
    if (wasNewCanvas || (!isCurrentlyDrawingThisPage && 
        (drawCanvas.width !== canvasWidth || drawCanvas.height !== canvasHeight))) {
      drawCanvas.width = canvasWidth;
      drawCanvas.height = canvasHeight;
    }
    
    // Always update display size (CSS, doesn't clear canvas)
    drawCanvas.style.width = `${rect.width}px`;
    drawCanvas.style.height = `${rect.height}px`;

    return drawCanvas;
  }, [pageRefs]);

  // Get drawing canvas for a specific page (create on-demand if needed)
  const getDrawingCanvas = useCallback((pageNum) => {
    const pageRef = pageRefs.current[pageNum - 1]?.current;
    if (!pageRef) return null;

    let drawCanvas = pageRef.querySelector(".pdf-draw-canvas");
    // Create canvas on-demand if it doesn't exist (for pages that haven't been visible yet)
    if (!drawCanvas && isDrawingMode) {
      drawCanvas = setupDrawingCanvasForPage(pageNum);
    }
    if (!drawCanvas) return null;

    // Use PDF canvas dimensions if available, otherwise use page container dimensions
    const pdfCanvas = pageRef.querySelector(".pdf-canvas");
    let rect;
    
    if (pdfCanvas && pdfCanvas.width > 0 && pdfCanvas.height > 0) {
      // PDF canvas is rendered, use its dimensions
      rect = pdfCanvas.getBoundingClientRect();
    } else {
      // PDF canvas not rendered yet, use page container dimensions
      // This allows drawing immediately on large PDFs
      rect = pageRef.getBoundingClientRect();
    }

    // Only update display size, not internal size (setting width/height clears canvas!)
    drawCanvas.style.width = `${rect.width}px`;
    drawCanvas.style.height = `${rect.height}px`;

    return drawCanvas;
  }, [pageRefs, isDrawingMode, setupDrawingCanvasForPage]);

  // Get saved drawing canvas for a specific page

  // Draw path on canvas
  const drawPath = useCallback((canvas, path, tool, color, size) => {
    if (!canvas || !path || path.length === 0) return;

    const ctx = canvas.getContext("2d");
    ctx.save();

    if (tool === "pen") {
      ctx.strokeStyle = color;
      ctx.lineWidth = size || 25;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
    } else if (tool === "highlighter") {
      ctx.strokeStyle = color;
      ctx.lineWidth = size || 15;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.globalAlpha = 0.4; // Semi-transparent for highlighter
    }

    ctx.beginPath();
    ctx.moveTo(path[0].x, path[0].y);
    for (let i = 1; i < path.length; i++) {
      ctx.lineTo(path[i].x, path[i].y);
    }
    ctx.stroke();
    ctx.restore();
  }, []);

  // Redraw all drawings for a page
  const redrawPage = useCallback((pageNum) => {
    const canvas = getDrawingCanvas(pageNum);
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const pageDrawings = drawings[pageNum] || [];
    pageDrawings.forEach((drawing) => {
      drawPath(canvas, drawing.path, drawing.tool, drawing.color, drawing.size);
    });
  }, [drawings, getDrawingCanvas, drawPath]);

  // Redraw pages when unsaved drawings change (but not while actively drawing)
  // Optimized: Only redraw rendered pages to avoid blocking lazy rendering
  useEffect(() => {
    // Skip redraw if actively drawing - the drawing is already on canvas from real-time drawing
    if (isDrawing) return;
    
    // Use requestAnimationFrame to batch redraws and avoid blocking
    requestAnimationFrame(() => {
      // Only redraw pages that have drawings and are likely rendered
      const pagesWithDrawings = Object.keys(drawings).map(Number);
      pagesWithDrawings.forEach((pageNum) => {
        // Check if page is rendered before redrawing
        const pageRef = pageRefs.current[pageNum - 1]?.current;
        if (pageRef) {
          const pdfCanvas = pageRef.querySelector(".pdf-canvas");
          // Only redraw if the PDF canvas exists (page is rendered)
          if (pdfCanvas && pdfCanvas.width > 0 && pdfCanvas.height > 0) {
            redrawPage(pageNum);
          }
        }
      });
    });
  }, [drawings, totalPages, redrawPage, isDrawing, pageRefs]);

  // Get page number from event target
  const getPageFromEvent = useCallback((e) => {
    for (let i = 0; i < pageRefs.current.length; i++) {
      const pageRef = pageRefs.current[i]?.current;
      if (pageRef && pageRef.contains(e.target)) {
        return i + 1;
      }
    }
    return null;
  }, [pageRefs]);

  // Get coordinates relative to canvas (also returns scale factor for brush size)
  const getCanvasCoordinates = useCallback((e, pageNum) => {
    const pageRef = pageRefs.current[pageNum - 1]?.current;
    if (!pageRef) return null;

    const drawCanvas = pageRef.querySelector(".pdf-draw-canvas");
    if (!drawCanvas) return null;

    // Use PDF canvas if available, otherwise use page container for coordinate calculation
    const pdfCanvas = pageRef.querySelector(".pdf-canvas");
    const referenceElement = (pdfCanvas && pdfCanvas.width > 0 && pdfCanvas.height > 0) 
      ? pdfCanvas 
      : pageRef;

    // Ensure reference element is ready
    if (!referenceElement) return null;
    
    const rect = referenceElement.getBoundingClientRect();
    
    // Get scale factor from canvas dimensions
    // Use PDF canvas dimensions if available, otherwise use display dimensions
    const canvasWidth = (pdfCanvas && pdfCanvas.width > 0) ? pdfCanvas.width : drawCanvas.width || rect.width;
    const canvasHeight = (pdfCanvas && pdfCanvas.height > 0) ? pdfCanvas.height : drawCanvas.height || rect.height;
    
    const scaleX = canvasWidth / rect.width;
    const scaleY = canvasHeight / rect.height;
    // Use average scale for brush size (usually scaleX ≈ scaleY)
    const scale = (scaleX + scaleY) / 2;

    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
      scale, // Include scale for brush size calculation
      isEstimated: !(pdfCanvas && pdfCanvas.width > 0 && pdfCanvas.height > 0) // Flag if using estimated dimensions
    };
  }, [pageRefs]);

  const handleMouseDown = useCallback((e) => {
    if (!isDrawingMode || selectedTool === null) return;

    const pageNum = getPageFromEvent(e);
    if (!pageNum) return;

    const coords = getCanvasCoordinates(e, pageNum);
    if (!coords) return;

    setIsDrawing(true);
    setCurrentPath([coords]);
    currentDrawingPageRef.current = pageNum;

    // Draw initial point/dot when mouse down
    const canvas = getDrawingCanvas(pageNum);
    if (canvas) {
      const ctx = canvas.getContext("2d");
      ctx.save();

      // Scale brush size to match canvas internal units
      const scaledBrushSize = (brushSize || 25) * (coords.scale || 1);

      if (selectedTool === "pen") {
        ctx.fillStyle = selectedColor;
        ctx.beginPath();
        ctx.arc(coords.x, coords.y, scaledBrushSize / 2, 0, Math.PI * 2);
        ctx.fill();
      } else if (selectedTool === "highlighter") {
        ctx.fillStyle = selectedColor;
        ctx.globalAlpha = 0.4;
        ctx.beginPath();
        ctx.arc(coords.x, coords.y, scaledBrushSize / 2, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.restore();
    }
  }, [isDrawingMode, selectedTool, brushSize, selectedColor, getPageFromEvent, getCanvasCoordinates, getDrawingCanvas]);

  const handleMouseMove = useCallback((e) => {
    // Update cursor position only when mouse is over a PDF page
    if (isDrawingMode && (selectedTool === "pen" || selectedTool === "highlighter")) {
      const pageNum = getPageFromEvent(e);
      if (pageNum) {
        const pageRef = pageRefs.current[pageNum - 1]?.current;
        if (pageRef) {
          // Check if mouse is actually over the page element
          const pageElement = e.target.closest(".pdf-page");
          if (pageElement === pageRef) {
            // Use requestAnimationFrame for smooth cursor movement
            requestAnimationFrame(() => {
              setCursorPosition({
                x: e.clientX,
                y: e.clientY,
                visible: true,
                pageNum,
                tool: selectedTool
              });
            });
          } else {
            // Mouse is not over a page, hide cursor
            setCursorPosition(prev => ({ ...prev, visible: false }));
          }
        }
      } else {
        // Not over any page, hide cursor
        setCursorPosition(prev => ({ ...prev, visible: false }));
      }
    }

    if (!isDrawing || currentPath.length === 0) return;

    // Use the page we started drawing on, instead of the current hovered page
    const pageNum = currentDrawingPageRef.current;
    if (!pageNum) return;

    const coords = getCanvasCoordinates(e, pageNum);
    if (!coords) return;

    // Get the last point BEFORE updating state (state updates are async)
    const lastPoint = currentPath[currentPath.length - 1];

    // Draw on canvas in real-time BEFORE updating state
    const canvas = getDrawingCanvas(pageNum);
    if (canvas && lastPoint) {
      const ctx = canvas.getContext("2d");
      ctx.save();

      // Scale brush size to match canvas internal units
      const scaledBrushSize = (brushSize || 25) * (coords.scale || 1);

      if (selectedTool === "pen") {
        ctx.strokeStyle = selectedColor;
        ctx.lineWidth = scaledBrushSize;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
      } else if (selectedTool === "highlighter") {
        ctx.strokeStyle = selectedColor;
        ctx.lineWidth = scaledBrushSize;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.globalAlpha = 0.4;
      }

      // Draw line from last point to new point
      ctx.beginPath();
      ctx.moveTo(lastPoint.x, lastPoint.y);
      ctx.lineTo(coords.x, coords.y);
      ctx.stroke();
      ctx.restore();
    }

    // Update state after drawing
    setCurrentPath((prev) => [...prev, coords]);
  }, [isDrawing, currentPath, selectedTool, selectedColor, brushSize, isDrawingMode, getPageFromEvent, getCanvasCoordinates, getDrawingCanvas]);

  const handleMouseUp = useCallback((e) => {
    if (!isDrawing || currentPath.length === 0) return;

    const pageNum = currentDrawingPageRef.current;
    if (!pageNum) return;

    // Get scale from the first point (all points have the same scale)
    const scale = currentPath[0]?.scale || 1;
    const scaledBrushSize = (brushSize || 25) * scale;

    // Save the drawing with scaled brush size
    setDrawings((prev) => {
      const pageDrawings = prev[pageNum] || [];
      return {
        ...prev,
        [pageNum]: [
          ...pageDrawings,
          {
            tool: selectedTool,
            color: selectedColor,
            path: [...currentPath],
            size: scaledBrushSize, // Store the scaled size
            type: "path",
          },
        ],
      };
    });

    setIsDrawing(false);
    setCurrentPath([]);
    currentDrawingPageRef.current = null;
  }, [isDrawing, currentPath, selectedTool, selectedColor, brushSize, getPageFromEvent]);

  // Initialize history with empty state if history is empty
  useEffect(() => {
    if (drawingHistory.length === 0) {
      setDrawingHistory([{}]);
      setHistoryIndex(0);
    }
  }, []);

  // Add to history when drawings change (but not during undo/redo or while actively drawing)
  useEffect(() => {
    // Skip if in undo/redo mode, actively drawing, or if state hasn't changed
    if (isUndoRedoRef.current || isDrawing) return;
    
    // Check if drawings actually changed (avoid duplicate history entries)
    const drawingsStr = JSON.stringify(drawings);
    if (lastHistoryStateRef.current === drawingsStr) return;
    
    // Only add to history if there are actual drawings
    const hasDrawings = Object.keys(drawings).length > 0 && 
      Object.values(drawings).some(pageDrawings => pageDrawings.length > 0);
    
    if (hasDrawings || lastHistoryStateRef.current !== null) {
      setHistoryIndex((prevIndex) => {
        setDrawingHistory((prevHistory) => {
          // Remove any history after current index (when user draws after undo)
          const newHistory = prevHistory.slice(0, prevIndex + 1);
          // Add new state to history
          newHistory.push(JSON.parse(JSON.stringify(drawings))); // Deep copy
          lastHistoryStateRef.current = drawingsStr;
          return newHistory;
        });
        return prevIndex + 1;
      });
    }
  }, [drawings, isDrawing]);

  // Notify parent when drawings change
  useEffect(() => {
    if (onDrawingsChange) {
      const hasDrawings = Object.keys(drawings).length > 0 && 
        Object.values(drawings).some(pageDrawings => pageDrawings.length > 0);
      onDrawingsChange(hasDrawings);
    }
  }, [drawings, onDrawingsChange]);

  // Undo: Go back in drawing history
  const undo = useCallback(() => {
    setHistoryIndex((prevIndex) => {
      if (prevIndex > 0) {
        const newIndex = prevIndex - 1;
        isUndoRedoRef.current = true;
        
        // Store current drawings state before undo (to redraw cleared pages)
        prevDrawingsBeforeUndoRedoRef.current = JSON.parse(JSON.stringify(drawings));
        
        // Get the previous state from history and update drawings
        setDrawingHistory((prevHistory) => {
          const previousState = prevHistory[newIndex];
          if (previousState) {
            // Update drawings with the previous state
            const newDrawings = JSON.parse(JSON.stringify(previousState)); // Deep copy
            setDrawings(newDrawings);
            // Update ref to match the new state
            lastHistoryStateRef.current = JSON.stringify(newDrawings);
          }
          return prevHistory; // History doesn't change on undo
        });
        
        return newIndex;
      }
      return prevIndex;
    });
  }, [drawings]);

  // Redo: Go forward in drawing history
  const redo = useCallback(() => {
    // Store current drawings state before redo (to redraw cleared pages)
    prevDrawingsBeforeUndoRedoRef.current = JSON.parse(JSON.stringify(drawings));
    
    setDrawingHistory((prevHistory) => {
      setHistoryIndex((prevIndex) => {
        if (prevIndex < prevHistory.length - 1) {
          const newIndex = prevIndex + 1;
          isUndoRedoRef.current = true;
          
          const nextState = prevHistory[newIndex];
          if (nextState) {
            // Update drawings with the next state
            const newDrawings = JSON.parse(JSON.stringify(nextState)); // Deep copy
            setDrawings(newDrawings);
            // Update ref to match the new state
            lastHistoryStateRef.current = JSON.stringify(newDrawings);
          }
          
          return newIndex;
        }
        return prevIndex;
      });
      return prevHistory; // History doesn't change on redo
    });
  }, [drawings]);

  // Reset undo/redo flag and redraw after state updates
  useEffect(() => {
    if (isUndoRedoRef.current) {
      // Use a small delay to ensure state updates are complete
      const timeoutId = setTimeout(() => {
        isUndoRedoRef.current = false;
        
        // Get pages from both previous and current state to ensure cleared pages are redrawn
        const prevPages = prevDrawingsBeforeUndoRedoRef.current 
          ? Object.keys(prevDrawingsBeforeUndoRedoRef.current).map(Number)
          : [];
        const currentPages = Object.keys(drawings).map(Number);
        
        // Combine and deduplicate page numbers
        const allPagesToRedraw = [...new Set([...prevPages, ...currentPages])];
        
        // Redraw all affected pages (including those that need to be cleared)
        allPagesToRedraw.forEach((pageNum) => {
          redrawPage(pageNum);
        });
        
        // Clear the previous state ref
        prevDrawingsBeforeUndoRedoRef.current = null;
      }, 10);
      
      return () => clearTimeout(timeoutId);
    }
  }, [drawings, historyIndex, redrawPage]);

  // Check if there are any unsaved drawings
  const hasUnsavedDrawings = useMemo(() => {
    return Object.keys(drawings).length > 0 && 
      Object.values(drawings).some(pageDrawings => pageDrawings.length > 0);
  }, [drawings]);

  // Check if undo is available (must have history AND unsaved drawings)
  const canUndo = historyIndex > 0 && hasUnsavedDrawings;
  
  // Check if redo is available (only needs history ahead, can restore even if current is empty)
  const canRedo = historyIndex < drawingHistory.length - 1;

  // Expose clearDrawings function to parent via ref (only clears unsaved)
  const clearDrawings = useCallback(() => {
    setDrawings({});
    setIsDrawing(false);
    setCurrentPath([]);
    currentDrawingPageRef.current = null;
    
    // Reset undo/redo history
    setDrawingHistory([{}]);
    setHistoryIndex(0);
    lastHistoryStateRef.current = null;
    
    // Clear all unsaved drawing canvases (saved drawings remain)
    if (pageRefs.current) {
      pageRefs.current.forEach((pageRef) => {
        const page = pageRef?.current;
        if (page) {
          const drawCanvas = page.querySelector(".pdf-draw-canvas");
          if (drawCanvas) {
            const ctx = drawCanvas.getContext("2d");
            ctx.clearRect(0, 0, drawCanvas.width, drawCanvas.height);
          }
        }
      });
    }
    
    // Notify parent that unsaved drawings are cleared
    if (onDrawingsChange) {
      onDrawingsChange(false);
    }
  }, [pageRefs, onDrawingsChange]);

  // Clear saved drawing canvases (called when PDF is reloaded after save)
  const clearSavedDrawings = useCallback(() => {
    // Clear all saved drawing canvases
    if (pageRefs.current) {
      pageRefs.current.forEach((pageRef) => {
        const page = pageRef?.current;
        if (page) {
          const savedCanvas = page.querySelector(".pdf-saved-draw-canvas");
          if (savedCanvas) {
            const ctx = savedCanvas.getContext("2d");
            ctx.clearRect(0, 0, savedCanvas.width, savedCanvas.height);
          }
        }
      });
    }
  }, [pageRefs]);

  useImperativeHandle(ref, () => ({
    clearDrawings,
    clearSavedDrawings,
    undo,
    redo,
    canUndo: () => {
      const hasUnsaved = Object.keys(drawings).length > 0 && 
        Object.values(drawings).some(pageDrawings => pageDrawings.length > 0);
      return historyIndex > 0 && hasUnsaved;
    },
    canRedo: () => {
      // Redo is available if there's history ahead (can restore even if current is empty)
      return historyIndex < drawingHistory.length - 1;
    },
  }), [clearDrawings, clearSavedDrawings, undo, redo, historyIndex, drawingHistory, drawings]);

  // Handle mouse leave to hide cursor
  const handleMouseLeave = useCallback((e) => {
    setCursorPosition(prev => ({ ...prev, visible: false }));
    
    // Check if we're leaving a PDF page (not just the container)
    const pageElement = e.target.closest(".pdf-page");
    if (pageElement) {
      // Only remove cursor class if leaving the page area
      pageElement.classList.remove("pdf-drawing-active");
    }
  }, []);


  // Attach event listeners when in drawing mode
  useEffect(() => {
    const container = document.querySelector(".pdf-pages");
    if (!container) return;

    if (!isDrawingMode) {
      setCursorPosition({ x: 0, y: 0, visible: false });
      container.classList.remove("pdf-drawing-active");
      return;
    }

    // Add event listeners
    container.addEventListener("mousedown", handleMouseDown);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    container.addEventListener("mouseleave", handleMouseLeave);
    
    // Add mouseenter/leave listeners to all PDF pages for cursor control
    const pages = container.querySelectorAll(".pdf-page");
    const pageListeners = [];
    
    pages.forEach(page => {
      const enterHandler = (e) => {
        if (isDrawingMode && (selectedTool === "pen" || selectedTool === "highlighter")) {
          page.classList.add("pdf-drawing-active");
        }
      };
      const leaveHandler = (e) => {
        page.classList.remove("pdf-drawing-active");
        // Hide cursor when leaving a page
        setCursorPosition(prev => ({ ...prev, visible: false }));
      };
      
      page.addEventListener("mouseenter", enterHandler);
      page.addEventListener("mouseleave", leaveHandler);
      pageListeners.push({ page, enterHandler, leaveHandler });
    });

    return () => {
      // Remove cursor class from all pages
      const allPages = container.querySelectorAll(".pdf-page");
      allPages.forEach(page => {
        page.classList.remove("pdf-drawing-active");
      });
      
      // Clean up page listeners
      pageListeners.forEach(({ page, enterHandler, leaveHandler }) => {
        page.removeEventListener("mouseenter", enterHandler);
        page.removeEventListener("mouseleave", leaveHandler);
      });
      
      container.removeEventListener("mousedown", handleMouseDown);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      container.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, [isDrawingMode, selectedTool, handleMouseDown, handleMouseMove, handleMouseUp, handleMouseLeave]);

  // Create and manage unsaved drawing canvases (only in drawing mode)
  // OPTIMIZED: Only create canvases for visible/rendered pages, use lazy creation for others
  useEffect(() => {
    if (!isDrawingMode || !pdfDoc || totalPages === 0) return;

    // Track which canvases we've created
    const createdCanvases = [];
    let cancelled = false;

    // Function to create canvas for a page (batched to avoid blocking)
    const createCanvasForPage = (pageNum) => {
      if (cancelled) return;
      const canvas = setupDrawingCanvasForPage(pageNum);
      if (canvas) {
        createdCanvases.push(canvas);
      }
    };

    // For large PDFs, only create canvases for visible/rendered pages initially
    // Use IntersectionObserver for lazy creation of other pages
    const pagesContainer = document.querySelector(".pdf-pages");
    if (!pagesContainer) return;

    // First, create canvases for pages that are likely visible (current page ± buffer)
    // This is much faster than creating all 944 canvases
    const visibleBuffer = 5; // Create canvases for ±5 pages around current
    const currentPageNum = currentDrawingPageRef.current || 1;
    
    // Create canvases for visible range immediately (batched with requestAnimationFrame)
    const visiblePages = [];
    for (let i = Math.max(1, currentPageNum - visibleBuffer); 
         i <= Math.min(totalPages, currentPageNum + visibleBuffer); 
         i++) {
      visiblePages.push(i);
    }

    // Batch create visible canvases to avoid blocking
    let batchIndex = 0;
    const createVisibleCanvasesBatch = () => {
      if (cancelled || batchIndex >= visiblePages.length) {
        // After visible pages, setup observer for lazy creation
        setupLazyCanvasCreation();
        return;
      }

      // Create 3 canvases per frame to balance performance
      const batchSize = 3;
      for (let i = 0; i < batchSize && batchIndex < visiblePages.length; i++) {
        createCanvasForPage(visiblePages[batchIndex]);
        batchIndex++;
      }

      requestAnimationFrame(createVisibleCanvasesBatch);
    };

    // Setup IntersectionObserver for lazy canvas creation
    let lazyObserver = null;
    const setupLazyCanvasCreation = () => {
      if (cancelled) return;

      lazyObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (cancelled) return;
            
            if (entry.isIntersecting) {
              const pageElement = entry.target;
              const pageNum = parseInt(pageElement.dataset.pageNum);
              if (pageNum && !pageElement.querySelector(".pdf-draw-canvas")) {
                // Create canvas when page comes into view
                createCanvasForPage(pageNum);
              }
            }
          });
        },
        {
          root: pagesContainer,
          rootMargin: "500px", // Create canvas 500px before page is visible
          threshold: 0.01
        }
      );

      // Observe all page containers
      pageRefs.current.forEach((ref, index) => {
        if (ref?.current && !cancelled) {
          const pageElement = ref.current;
          pageElement.dataset.pageNum = (index + 1).toString();
          lazyObserver.observe(pageElement);
        }
      });
    };

    // Start creating visible canvases
    requestAnimationFrame(createVisibleCanvasesBatch);

    // Redraw unsaved drawings after canvas resize
    // This is important when PDF is reloaded (after save) - canvases get resized
    // Also redraw when PDF canvas finishes rendering (for large PDFs)
    const redrawTimeout = setTimeout(() => {
      if (cancelled) return;
      // OPTIMIZED: Only check pages that have drawings (not all 944 pages)
      const pagesWithDrawings = Object.keys(drawings).map(Number);
      pagesWithDrawings.forEach((pageNum) => {
        const pageRef = pageRefs.current[pageNum - 1]?.current;
        if (!pageRef) return;
        const drawCanvas = pageRef.querySelector(".pdf-draw-canvas");
        if (drawCanvas && drawings[pageNum] && drawings[pageNum].length > 0) {
          redrawPage(pageNum);
        }
      });
    }, 100);
    
    // Also listen for PDF canvas rendering completion and adjust drawing canvas size
    // This handles the case where PDF canvas renders after drawing canvas is created
    // OPTIMIZED: Only check pages that have drawing canvases
    const checkAndResizeCanvases = () => {
      if (cancelled) return;
      
      // Only check pages that have drawing canvases (much faster)
      pageRefs.current.forEach((ref, index) => {
        if (cancelled) return;
        const pageRef = ref?.current;
        if (!pageRef) return;
        
        const drawCanvas = pageRef.querySelector(".pdf-draw-canvas");
        if (!drawCanvas) return; // Skip pages without drawing canvas
        
        const pdfCanvas = pageRef.querySelector(".pdf-canvas");
        const pageNum = index + 1;
        
        if (pdfCanvas && pdfCanvas.width > 0 && pdfCanvas.height > 0) {
          // PDF canvas is now rendered, resize drawing canvas to match
          const isCurrentlyDrawingThisPage = currentDrawingPageRef.current === pageNum;
          
          if (!isCurrentlyDrawingThisPage && 
              (drawCanvas.width !== pdfCanvas.width || drawCanvas.height !== pdfCanvas.height)) {
            // Store current drawings before resize (resize clears canvas)
            const pageDrawings = drawings[pageNum] || [];
            
            // Resize canvas
            drawCanvas.width = pdfCanvas.width;
            drawCanvas.height = pdfCanvas.height;
            
            // Redraw drawings with correct scale
            if (pageDrawings.length > 0) {
              redrawPage(pageNum);
            }
          }
        }
      });
    };
    
    // Check periodically for PDF canvas rendering (for large PDFs)
    // Reduced frequency for better performance
    const resizeInterval = setInterval(checkAndResizeCanvases, 500);

    // Cleanup: remove unsaved drawing canvases when mode is disabled
    return () => {
      cancelled = true;
      clearTimeout(redrawTimeout);
      clearInterval(resizeInterval);
      if (lazyObserver) {
        lazyObserver.disconnect();
      }
      if (!isDrawingMode) {
        createdCanvases.forEach((canvas) => {
          if (canvas && canvas.parentNode) {
            canvas.parentNode.removeChild(canvas);
          }
        });
      }
    };
  }, [isDrawingMode, pdfDoc, totalPages, pageRefs, drawings, redrawPage, setupDrawingCanvasForPage]);

  // Render cursor for drawing tools
  return (
    <>
      {isDrawingMode && cursorPosition.visible && (selectedTool === "pen" || selectedTool === "highlighter") && (
        <div
          ref={cursorRef}
          className={`pdf-draw-cursor pdf-draw-cursor-${selectedTool}`}
          data-theme={theme}
          style={{
            position: "fixed",
            left: `${cursorPosition.x}px`,
            top: `${cursorPosition.y}px`,
            width: `${brushSize || 25}px`,
            height: `${brushSize || 25}px`,
            borderRadius: "50%",
            border: `2px solid ${selectedColor}`,
            backgroundColor: selectedTool === "highlighter" 
              ? `${selectedColor}66` 
              : "transparent",
            pointerEvents: "none",
            zIndex: 10000,
            transform: "translate(-50%, -50%)",
          }}
        />
      )}
    </>
  );
});

PDFDrawCanvas.displayName = "PDFDrawCanvas";

export default PDFDrawCanvas;

